import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Search, Download, Trash2, RefreshCw, Mail, MailX, Users, CheckCircle, MapPin, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BRAND } from '@/data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'euroelite_admin_token';

const AdminLogin = ({ onSuccess }) => {
  const [key, setKey] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { key });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      toast.success('Login successful');
      onSuccess();
    } catch {
      toast.error('Invalid admin key');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login" data-testid="admin-login">
      <form onSubmit={submit} className="admin-login-box">
        <img src={BRAND.logo} alt="logo" />
        <h2 className="display-font">Admin Dashboard</h2>
        <p>EuroElite Leads Management</p>
        <div className="form-field" style={{ textAlign: 'left' }}>
          <label className="form-label">Admin Access Key</label>
          <input
            type="password" required
            value={key} onChange={(e) => setKey(e.target.value)}
            className="form-input"
            placeholder="Enter admin key"
            data-testid="admin-key-input"
          />
        </div>
        <button
          type="submit" disabled={loading}
          className="form-submit"
          style={{ width: '100%', marginTop: '20px' }}
          data-testid="admin-login-btn"
        >
          {loading ? 'Logging in...' : 'Access Dashboard'}
        </button>
      </form>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="admin-stat-card">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
      <h4>{label}</h4>
      <Icon size={18} color="var(--gold)" />
    </div>
    <div className="value">{value}</div>
  </div>
);

const AdminPage = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem(TOKEN_KEY);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const headers = { 'X-Admin-Key': token };
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers, params: search ? { search } : {} }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [token, search]);

  useEffect(() => { if (authed) fetchData(); }, [authed, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    navigate('/admin');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: { 'X-Admin-Key': token } });
      toast.success('Lead deleted');
      fetchData();
    } catch {
      toast.error('Delete failed');
    }
  };

  const exportCSV = () => {
    if (!leads.length) return toast.error('No data to export');
    const headers_csv = ['ID', 'Name', 'Phone', 'Email', 'City', 'Degree Level', 'Course', 'Country', 'Message', 'Source', 'Email Status', 'Date'];
    const rows = leads.map((l) => [
      l.id, l.full_name, l.phone, l.email, l.city || 'N/A',
      l.degree_level || 'N/A',
      l.course || 'N/A', l.country || 'N/A',
      (l.message || '').replace(/"/g, '""').replace(/\n/g, ' '),
      l.source || '', l.email_sent ? 'Sent' : 'Pending',
      new Date(l.timestamp).toLocaleString(),
    ]);
    const csv = [headers_csv, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `euroelite-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <div className="container">
        <div className="admin-header">
          <div>
            <h1 className="display-font">Leads <span className="gold-gradient">Dashboard</span></h1>
            <p style={{ color: 'var(--text-muted)', marginTop: '4px' }}>Manage all counselling enquiries</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button className="btn-secondary" onClick={() => navigate('/')} data-testid="admin-home-btn">View Site</button>
            <button className="btn-secondary" onClick={handleLogout} data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        {stats && (
          <div className="admin-stats">
            <StatCard icon={Users} label="Total Leads" value={stats.total_leads} />
            <StatCard icon={CheckCircle} label="Emails Sent" value={stats.emails_sent} />
            <StatCard icon={BookOpen} label="Top Course" value={stats.by_course[0]?.name || '—'} />
            <StatCard icon={MapPin} label="Top Country" value={stats.by_country[0]?.name || '—'} />
          </div>
        )}

        <div className="admin-controls">
          <div className="form-field" style={{ flex: 1, minWidth: '260px' }}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-input"
              placeholder="Search by name, email, phone, or city..."
              data-testid="admin-search-input"
            />
          </div>
          <button className="btn-secondary" onClick={fetchData} disabled={loading} data-testid="admin-refresh-btn">
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn-primary" onClick={exportCSV} data-testid="admin-export-btn">
            <Download size={14} /> Export CSV
          </button>
        </div>

        <div className="leads-table" data-testid="admin-leads-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>City</th>
                <th>Degree</th>
                <th>Course</th>
                <th>Country</th>
                <th>Source</th>
                <th>Email</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                    {loading ? 'Loading...' : 'No leads yet. Submissions will appear here.'}
                  </td>
                </tr>
              ) : leads.map((lead) => (
                <tr key={lead.id} data-testid={`admin-lead-${lead.id}`}>
                  <td><strong style={{ color: 'var(--text-primary)' }}>{lead.full_name}</strong></td>
                  <td>
                    <div style={{ fontSize: '13px' }}>{lead.email}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{lead.phone}</div>
                  </td>
                  <td>{lead.city || 'N/A'}</td>
                  <td>{lead.degree_level || 'N/A'}</td>
                  <td>{lead.course || 'N/A'}</td>
                  <td>{lead.country || 'N/A'}</td>
                  <td><span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{lead.source || '—'}</span></td>
                  <td>
                    {lead.email_sent
                      ? <span className="lead-badge success"><Mail size={11} /> Sent</span>
                      : <span className="lead-badge pending"><MailX size={11} /> Pending</span>}
                  </td>
                  <td style={{ fontSize: '12px' }}>{new Date(lead.timestamp).toLocaleString()}</td>
                  <td>
                    <button
                      className="icon-btn danger"
                      onClick={() => handleDelete(lead.id)}
                      data-testid={`admin-delete-${lead.id}`}
                      aria-label="Delete lead"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
